import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import './QuestionnaireSessionFrame.scss';
import { questionnaireInputFieldType, questionnaireQuestionTypes } from "../QuestionnaireConstants";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import { QuestionnaireSessionReactContext } from "../../../contexts/QuestionnaireContext/QuestionnaireSessionContext";
import { get } from "lodash";
import { I18nReactContext } from "../../../contexts/I18nContext";
import { useContextSelector } from "use-context-selector";
import { RatingScaleDisplay } from "../RatingScaleDisplay/RatingScaleDisplay";
import { AnswerEvents } from "../AnswerEventSystem";

export const QuestionnaireSessionFrame = () => {
    const currentQuestionIndex = useContextSelector(QuestionnaireSessionReactContext, context => context.currentQuestionIndex);
    const sessionQuestions = useContextSelector(QuestionnaireSessionReactContext, context => context.sessionQuestions);
    const processAnswerEvent = useContextSelector(QuestionnaireSessionReactContext, context => context.processAnswerEvent);
    const [question, setQuestion] = useState({});
    const [currentAnswers, setCurrentAnswers] = useState([]);
    const [textResponse, setTextResponse] = useState('');
    const [ratingValue, setRatingValue] = useState(null);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const { i18n } = useContext(I18nReactContext);
    
    // Track previous question ID to detect actual question navigation (not just data updates)
    const previousQuestionIdRef = useRef(null);


    useEffect(() => {
        if (sessionQuestions && sessionQuestions.length > 0) {
            const currentQuestion = get(sessionQuestions, currentQuestionIndex, {});
            const currentQuestionId = get(currentQuestion, 'id');
            
            setQuestion(currentQuestion);
            setCurrentAnswers(get(currentQuestion, 'userAnswers', []));
            setTextResponse(get(currentQuestion, 'textResponse', ''));
            setRatingValue(get(currentQuestion, 'ratingValue', null));
            
            // Only reset image loading state when navigating to a DIFFERENT question
            // (not when the same question's data changes, e.g., from answering)
            // This prevents the image from disappearing when the user answers - 
            // the browser caches the image so onLoad won't fire again if we reset state
            if (currentQuestionId !== previousQuestionIdRef.current) {
                setImageLoaded(false);
                setImageError(false);
                previousQuestionIdRef.current = currentQuestionId;
            }
        }
    }, [currentQuestionIndex, sessionQuestions]);

    const handleOptionSelect = ({ answerId }) => {
        processAnswerEvent(AnswerEvents.OPTION_SELECTED, { optionId: answerId });
    };

    const handleTextResponseChange = (event) => {
        const value = event.target.value;
        setTextResponse(value);
        processAnswerEvent(AnswerEvents.TEXT_CHANGED, { text: value });
    };

    const handleRatingSelect = (rating) => {
        setRatingValue(rating);
        processAnswerEvent(AnswerEvents.RATING_SELECTED, { rating });
    };

    const renderQuestionInput = () => {
        const questionType = get(question, 'type');
        const options = get(question, 'options', []);

        switch (questionType) {
            case questionnaireQuestionTypes.FREE_TEXT:
                return (
                    <div className="questionFrameTextInput" data-testid="free-text-input-container">
                        <textarea
                            value={textResponse}
                            onChange={handleTextResponseChange}
                            placeholder={String(i18n.translate('surveySession.placeHolders.FREE_TEXT'))}
                            rows={4}
                            className="questionFrameTextArea"
                            data-testid="free-text-textarea"
                        />
                    </div>
                );

            case questionnaireQuestionTypes.RATING_SCALE:
                const ratingConfig = get(question, 'ratingScaleConfig', {});
                const minValue = get(ratingConfig, 'minValue', 1);
                const maxValue = get(ratingConfig, 'maxValue', 5);
                const minLabel = get(ratingConfig, 'minLabel', i18n.translate('questionnaireQuestions.survey.ratingScale.defaultMinLabel'));
                const maxLabel = get(ratingConfig, 'maxLabel', i18n.translate('questionnaireQuestions.survey.ratingScale.defaultMaxLabel'));

                return (
                    <div
                        className="rating-scale-container"
                        data-testid="rating-scale-container"
                    >
                        <RatingScaleDisplay
                            minValue={minValue}
                            maxValue={maxValue}
                            minLabel={String(minLabel)}
                            maxLabel={String(maxLabel)}
                            selectedValue={ratingValue}
                            onValueSelect={handleRatingSelect}
                            isPreview={false}
                        />
                    </div>
                );

            default:
                // Multiple choice and checkbox questions
                return (
                    <div className="questionFrameAnswers" data-testid="multiple-choice-options">
                        {options.map((option) => {
                            const optionId = get(option, 'id', '');
                            return (
                                <div
                                    key={optionId}
                                    className={`questionFrameAnswer ${currentAnswers.includes(optionId) ? 'selected' : ''}`}
                                    onClick={() => handleOptionSelect({ answerId: optionId })}
                                    data-testid={`answer-option-${optionId}`}
                                >
                                    <div className="questionFrameAnswerSelector">
                                        <input
                                            type={questionnaireInputFieldType[questionType]}
                                            name="question-answer"
                                            checked={currentAnswers.includes(optionId)}
                                            readOnly
                                            data-testid={`answer-input-${optionId}`}
                                        />
                                    </div>
                                    <div className="questionFrameAnswerText" data-testid={`answer-text-${optionId}`}>
                                        {get(option, 'text', '')}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
        }
    };




    const hasImage = !!get(question, 'questionMediaUrl');

    return (!question || !get(question, 'text') ? <LoadingSpinner /> :
        <div className="questionFrame" data-testid="question-frame">
            <h2 className="questionFrameQuestion" data-testid="question-text">{get(question, 'text', '')}</h2>
            
            <div className={`questionFrameContent ${hasImage ? 'with-image' : ''}`} data-testid="question-content">
            <div className="question-answers-panel" data-testid="question-answers-panel">
                    {renderQuestionInput()}
                </div>
                {hasImage && (
                    <div className="question-image-panel" data-testid="question-image-panel">
                        <div className="question-image" data-testid="question-image">
                            {/* Loading skeleton - shown while image is loading */}
                            {!imageLoaded && !imageError && (
                                <div className="image-loading-skeleton" data-testid="image-loading-skeleton">
                                    <LoadingSpinner local />
                                </div>
                            )}
                            
                            {/* Error state - shown if image fails to load */}
                            {imageError && (
                                <div className="image-error" data-testid="image-error">
                                    {i18n.translate('questionnaireSession.imageLoadError')}
                                </div>
                            )}
                            
                            {/* Actual image - hidden until loaded */}
                            <img 
                                src={get(question, 'questionMediaUrl')} 
                                alt="question-media"
                                className={`question-media ${imageLoaded ? 'loaded' : 'loading'}`}
                                onLoad={() => setImageLoaded(true)}
                                onError={() => setImageError(true)}
                                style={{ display: imageLoaded ? 'block' : 'none' }}
                            />
                        </div>
                    </div>
                )}
                
               
            </div>
        </div>
    );
};

